import { useDebouncedCallback } from "use-debounce";
import { useLayoutEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Table, TableInstance } from "@monolith-forensics/monolith-ui";
import type { ButtonProps } from "@monolith-forensics/monolith-ui";
import type { WithTestId } from "@/types/testid";

import { TimelineEventDefinitions } from "../Definitions";
import { AnalysisAPI } from "@/api";
import { TimelineEvent } from "../TimelineEvents.types";
import { getDateFormat } from "../../../utils/date-format";
import { useTimelineFilterDefinitions } from "../Hooks";
import {
  resolveFilterState,
  useClearTransientTableState,
  useTableState,
} from "@/Features/_shared";

const queryKeyPrefix = ["timeline-events", "list"];
const pageSize = 50;
// Not "caseTimeline": the legacy Trace-Indicators grid still reads that key
// (expecting a legacy `cols`-shaped blob), so a distinct key keeps the two
// tables' persisted state independent.
const stateStoreKey = "case:timeline:table";

export type TimelineEventsTableRef = {
  refresh: () => void;
};

export const TimelineEventsTable: React.FC<{
  tableRef?: React.MutableRefObject<TimelineEventsTableRef | undefined>;
  query: { case_id?: number | string | null };
  onAdd?: () => void;
  onLoaded?: () => void;
  onActionButtonClick?: (rowData: TimelineEvent) => void;
}> = ({ tableRef, query = {}, onAdd, onLoaded, onActionButtonClick }) => {
  const tableInstanceRef = useRef<TableInstance>();

  // The stateStoreKey is global so the column layout follows the user across
  // cases, but search/filter typed in one case must not restore in another.
  useClearTransientTableState(stateStoreKey);

  // Passing {} instead of `query`: this API takes `order`/`sort` rather than
  // `sort_by`/`sort_dir`, so tableState.resolvedQuery is unused and _query is
  // built from the state slices below.
  const tableState = useTableState({}, stateStoreKey);

  const _query = {
    ...query,
    order: tableState.sortState?.dataField,
    sort: tableState.sortState?.dir,
    filter: tableState.filterState,
    search: tableState.search,
    page_size: pageSize,
  };

  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isFetched,
  } = useInfiniteQuery({
    queryKey: [queryKeyPrefix, _query],
    queryFn: ({ pageParam }) =>
      AnalysisAPI.TimelineEvents.get({
        ..._query,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page - 1 === 0) return null;
      return firstPage.page - 1;
    },
    initialPageParam: 1,
    placeholderData: (data) => data,
  });

  const records =
    data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback((value) => {
    tableState.setSearchState(value);
  }, 300);

  const handleExportTable = (option: string) => {
    let columns = (tableInstanceRef.current?.columnState ?? []).map((c) => {
      return { ...c, header: c.caption };
    });

    if (option === "export:visible") {
      columns = columns.filter((c) => c.visible !== false);
    }

    AnalysisAPI.TimelineEvents.exportList({
      query: _query,
      type: "xlsx",
      columns,
      date_format: getDateFormat({ isMoment: true, includeTime: true }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).then((res) => {
      const { signedUrl, filename } = res;
      const el = document.createElement("a");
      el.href = signedUrl.replace(
        "http://localhost:3000",
        "http://localhost:3001"
      );
      el.download = filename;
      el.click();
      // remove snackbar
      el.remove();
    });
  };

  const loading = isLoading || !isFetched;

  if (tableRef) {
    tableRef.current = {
      refresh: refetch,
    };
  }

  useLayoutEffect(() => {
    if (isFetched) {
      onLoaded?.();
    }
  }, [isFetched, onLoaded]);

  const filters = useTimelineFilterDefinitions();

  return (
    // Owned anchor for specs: the table internals are the npm package's DOM.
    <div data-testid="timeline-table" style={{ display: "contents" }}>
      <Table
        columnProps={TimelineEventDefinitions}
        data={records}
        tableInstanceRef={tableInstanceRef}
        loading={loading}
        virtualized={true}
        manualSearch={true}
        manualSorting={true}
        manualExport={true}
        manualFiltering={true}
        enableActionButton={true}
        onActionButtonClick={onActionButtonClick}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        stateStorage={{
          enabled: true,
          type: "localStorage",
          key: stateStoreKey,
        }}
        enableSorting={true}
        enableColumnReorder={true}
        enableColumnResize={true}
        tableMenuOptions={{
          enabled: true,
          addButtonOptions: {
            enabled: true,
            label: "+ Create Event",
            buttonOptions: {
              "data-testid": "timeline-create-event-button",
            } as WithTestId<ButtonProps>,
            onClick: onAdd,
          },
          filterOptions: {
            enabled: true,
            filterDefinitions: filters,
            onFilterChange: (filterQuery) => {
              tableState.setFilterState(resolveFilterState(filterQuery));
            },
          },
          searchOptions: {
            enabled: true,
            placeholder: "Search Events",
            onSearch: debouncedSearch,
          },
          exportOptions: {
            enabled: true,
            onExport: handleExportTable,
          },
          columnSelectorOptions: {
            enabled: true,
          },
        }}
      />
    </div>
  );
};
